import { useMemo, useState, type ReactElement } from 'react';
import { IconAlertTriangle, IconArrowRight, IconCalendarDue, IconCheck, IconClock, IconCurrentLocation, IconDownload, IconFilterOff, IconMap, IconPlus, IconRoute, IconSearch, IconUpload, IconBuildingWarehouse, IconAnchor, IconUsers, IconBuildingBridge, IconChevronDown, IconChartBar } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { Page, PageHeader } from '@hana/shell';
import { Button, StateView, Table, useToast, type Column, type ScreenState } from '@hana/ui';
import { useNavigate } from '@tanstack/react-router';
import {
  CG_LABEL, CG_TIP, DP, EMPTY_FILTER, STATUS_LABEL, TOTAL, WH, customersOf, daysToExpiry, expiring, filterRoutes, fmtFull, fmtHM, fmtKm, fromName, missingXY, routeChips, shortDate, toggle, whName,
  type RouteFilter, type RouteRow, type RouteStatus,
} from './routes';
import { useT } from '@hana/i18n';
import './i18n';
import { useNarrow } from './useWide';
import s from './routes.module.css';

// Mint-TMS-Routes (desktop 1536) · bảng làm việc nhiều tuyến. Chỉ đọc + thao tác giả lập (toast).
// Chưa làm: ngăn chi tiết / sửa tuyến, nhập Excel, chọn nhiều + thanh thao tác hàng loạt, tìm nhanh Ctrl K ở màn này.

const STATUS_DOT: Record<RouteStatus, string> = { ok: 'var(--d-green)', pend: 'var(--d-amber)', off: 'var(--d-gray)' };
const CG_DOT: Record<RouteRow['cg'], string> = { RF: 'var(--d-violet)', DC: 'var(--d-blue)' };

type Key = 'from' | 'wh' | 'tier' | 'status' | 'gps';
interface Opt { value: string; label: string; n: number; head?: boolean }

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

function FilterMenu({ label, icon, cur, opts, open, onToggle, onPick, onClear }: { label: string; icon: ReactElement; cur: readonly string[]; opts: Opt[]; open: boolean; onToggle: () => void; onPick: (v: string) => void; onClear: () => void }) {
  const t = useT();
  const names = opts.filter((o) => !o.head && cur.includes(o.value)).map((o) => o.label);
  const text = !names.length ? label : names.length > 2 ? `${label} · ${names.length}` : names.join(', ');
  return (
    <div className={s.fWrap}>
      <button type="button" className={`${s.fBtn} ${names.length ? s.on : ''}`} aria-haspopup="listbox" aria-expanded={open} title={names.length ? `${label}: ${names.join(', ')}` : label} onClick={onToggle}>
        {icon}{text}<IconChevronDown size={14} aria-hidden="true" />
      </button>
      {open && (
        <>
          <div className={s.scrim} aria-hidden="true" onClick={onToggle} />
          <div className={s.pop} role="listbox" aria-multiselectable="true" aria-label={label}>
            {opts.map((o) => o.head ? <div key={`h-${o.label}`} className={s.head}>{o.label}</div> : (
              <button key={o.value} type="button" role="option" aria-selected={cur.includes(o.value)} className={s.opt} onClick={() => onPick(o.value)}>
                <span className={s.cb}>{cur.includes(o.value) && <IconCheck size={13} stroke={3} aria-hidden="true" />}</span>
                <span>{o.label}</span>
                <em>{o.n}</em>
              </button>
            ))}
            {names.length > 0 && <button type="button" className={s.clr} onClick={onClear}><IconFilterOff size={14} aria-hidden="true" />{t('tms.clearFilter')}</button>}
          </div>
        </>
      )}
    </div>
  );
}

export function RoutesPage() {
  const toast = useToast();
  const t = useT();
  const navigate = useNavigate();
  const { data, error, isLoading, isError, refetch } = useList('tmsRouteRow', { limit: 500 });
  const forced = readState();
  const list = useMemo(() => data?.items ?? [], [data]);
  const [f, setF] = useState<RouteFilter>(EMPTY_FILTER);
  const [pop, setPop] = useState<Key | null>(null);
  const narrow = useNarrow();
  const [dense, setDense] = useState(false);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (list.length === 0) state = 'empty';

  const rows = useMemo(() => filterRoutes(list, f), [list, f]);
  const chips = routeChips(list);
  const cnt = (skip: keyof RouteFilter, fn: (x: RouteRow) => boolean) => filterRoutes(list, f, skip).filter(fn).length;
  const anyF = f.cg !== 'all' || f.from.length || f.wh.length || f.tier.length || f.status.length || f.gps.length || f.expiring || f.q.trim() !== '';
  const set = (p: Partial<RouteFilter>) => setF((x) => ({ ...x, ...p }));

  const fromOpts: Opt[] = Object.keys(DP).filter((c) => list.some((x) => x.from === c)).map((c) => ({ value: c, label: DP[c]!, n: cnt('from', (x) => x.from === c) }));
  const custs = [...new Set(list.flatMap(customersOf))].sort();
  const whOpts: Opt[] = [
    { value: '#k', label: 'Khách', n: 0, head: true },
    ...custs.map((c) => ({ value: c, label: c, n: cnt('wh', (x) => customersOf(x).includes(c)) })),
    { value: '#w', label: 'Kho', n: 0, head: true },
    ...Object.keys(WH).filter((c) => list.some((x) => x.warehouses.includes(c))).map((c) => ({ value: c, label: whName(c), n: cnt('wh', (x) => x.warehouses.includes(c)) })),
  ];
  const tierOpts: Opt[] = ['Tier1', 'Tier2', 'Tier3', 'Tier4'].map((t) => ({ value: t, label: t, n: cnt('tier', (x) => x.tier === t) }));
  const stOpts: Opt[] = (['pend', 'ok', 'off'] as const).map((k) => ({ value: k, label: STATUS_LABEL[k], n: cnt('status', (x) => x.status === k) }));
  const gpsOpts: Opt[] = [{ value: 'yes', label: 'Có GPS', n: cnt('gps', (x) => !!x.gps) }, { value: 'no', label: 'Chưa có GPS', n: cnt('gps', (x) => !x.gps) }];
  const menu = (key: Key, label: string, icon: ReactElement, cur: readonly string[], opts: Opt[]) => (
    <FilterMenu key={key} label={label} icon={icon} cur={cur} opts={opts} open={pop === key} onToggle={() => setPop(pop === key ? null : key)}
      onPick={(v) => set({ [key]: toggle(cur, v) } as Partial<RouteFilter>)} onClear={() => set({ [key]: [] } as Partial<RouteFilter>)} />
  );

  const cols: Column<RouteRow>[] = [
    { key: 'code', header: 'Mã', width: 92, cell: (x) => <span className={s.code}>{x.code}</span> },
    {
      key: 'route', header: 'Tuyến', cell: (x) => {
        const to = x.warehouses[0]!;
        const extra = x.warehouses.length - 1;
        const tip = [x.from, ...x.warehouses, x.from].map((c) => DP[c] ?? whName(c)).join(' → ');
        return (
          <span className={s.route} title={tip}>
            <span>{fromName(x)}</span>
            <IconArrowRight size={14} aria-hidden="true" />
            <span>{whName(to)}</span>
            {extra > 0 && <span className={s.more} title={`Ghép ${x.warehouses.length} kho: ${x.warehouses.map(whName).join(', ')}`}>{`+${extra} kho`}</span>}
            {missingXY(x) && <span className={s.miss} role="img" aria-label="Kho thiếu toạ độ" title="Kho thiếu toạ độ"><IconAlertTriangle size={14} aria-hidden="true" /></span>}
          </span>
        );
      },
    },
    { key: 'cg', header: 'Loại', width: 76, cell: (x) => <span className={s.pill} title={CG_TIP[x.cg]}><i style={{ background: CG_DOT[x.cg] }} />{CG_LABEL[x.cg]}</span> },
    { key: 'km', header: 'Km', width: 72, align: 'right', cell: (x) => <span className={s.num}>{fmtKm(x.km)}</span> },
    {
      key: 'tm', header: 'TG chạy TB', width: 124, cell: (x) => {
        const g = x.gps;
        return (
          <span className={s.tm} title={g ? `Thời gian chạy TB ${fmtFull(g[0])} · ${g[3]} chuyến GPS` : `Chưa có chuyến GPS · ước tính ${fmtFull(x.est)}`} style={{ color: g ? 'var(--tx)' : 'var(--lb)' }}>
            {g ? <IconClock size={14} aria-hidden="true" /> : <IconCurrentLocation size={14} aria-hidden="true" />}
            <b>{g ? fmtHM(g[0]) : `≈${fmtHM(x.est)}`}</b>
            {g && <small>{`· ${g[3]}`}</small>}
          </span>
        );
      },
    },
    { key: 'toll', header: 'Trạm · lượt', width: 96, cell: (x) => <span className={s.tm} title={x.tolls ? `${x.tolls} trạm · ${x.passes} lượt` : 'Chưa gắn trạm'} style={{ color: x.tolls ? 'var(--tx2)' : 'var(--lb)' }}><IconBuildingBridge size={14} aria-hidden="true" />{x.tolls ? `${x.tolls} · ${x.passes}` : '—'}</span> },
    { key: 'tier', header: 'Tier', width: 70, cell: (x) => <span className={s.tx2}>{x.tier}</span> },
    {
      key: 'st', header: 'Trạng thái', width: 128, cell: (x) => (
        <span className={`${s.st} ${x.status === 'off' ? s.stOff : ''}`}>
          <i style={{ background: STATUS_DOT[x.status] }} />
          <span>{STATUS_LABEL[x.status]}</span>
          {expiring(x) && <span className={s.expI} role="img" aria-label={`Giá TRF hết hạn ${x.priceTo} · ${daysToExpiry(x)} ngày`} title={`Giá TRF hết hạn ${x.priceTo} · ${daysToExpiry(x)} ngày`}><IconCalendarDue size={14} aria-hidden="true" /></span>}
        </span>
      ),
    },
    { key: 'upd', header: 'Cập nhật', width: 132, cell: (x) => <span className={s.upd} title={`${x.by} · ${x.at}`}><span className={s.av} aria-hidden="true">{x.by.slice(0, 2).toUpperCase()}</span>{shortDate(x.at)}</span> },
  ];

  const allOn = rows.length > 0 && rows.every((x) => sel.has(x.code));
  const togAll = () => setSel(allOn ? new Set() : new Set(rows.map((x) => x.code)));
  const togOne = (c: string) => setSel((p) => { const n = new Set(p); if (n.has(c)) n.delete(c); else n.add(c); return n; });
  const bars = (k: Key, label: string, icon: ReactElement, cur: readonly string[], items: Opt[]) => {
    const mx = Math.max(1, ...items.map((i) => i.n));
    return (
      <div key={label} className={s.ovG}>
        <div className={s.ovH}>{icon}{label}</div>
        {items.map((i) => (
          <button key={i.value} type="button" className={s.ovB} aria-pressed={cur.includes(i.value)} title={`${i.label} · ${i.n} tuyến`} onClick={() => set({ [k]: toggle(cur, i.value) } as Partial<RouteFilter>)}>
            <span className={s.ovK}>{i.label}</span>
            <span className={s.ovT} aria-hidden="true"><span style={{ width: `${Math.round((i.n / mx) * 100)}%` }} /></span>
            <b>{i.n}</b>
          </button>
        ))}
      </div>
    );
  };
  const bodyPad = (
    <div className={s.pad}>
      <section className={s.lc} aria-label="Danh sách tuyến">
        <div className={s.lcHead}>
          <div className={s.bar}>
            <label className={s.search}>
              <IconSearch size={15} stroke={2.2} aria-hidden="true" />
              <input aria-label="Tìm mã tuyến, kho, khách" placeholder="Mã tuyến, kho, khách" value={f.q} onChange={(e) => set({ q: e.target.value })} />
            </label>
            <div className={s.seg} role="group" aria-label="Loại hàng">
              {([['all', 'Tất cả'], ['RF', 'RF'], ['DC', 'Khô']] as const).map(([k, label]) => (
                <button key={k} type="button" aria-pressed={f.cg === k} onClick={() => set({ cg: k })}>{label}<span>{cnt('cg', (x) => k === 'all' || x.cg === k)}</span></button>
              ))}
            </div>
          </div>
          <div className={s.bar}>
            {menu('from', 'Điểm đi', <IconAnchor size={15} aria-hidden="true" />, f.from, fromOpts)}
            {menu('wh', 'Kho / khách', <IconBuildingWarehouse size={15} aria-hidden="true" />, f.wh, whOpts)}
            {menu('tier', 'Tier', <IconUsers size={15} aria-hidden="true" />, f.tier, tierOpts)}
            {menu('status', 'Trạng thái', <IconCheck size={15} aria-hidden="true" />, f.status, stOpts)}
            {menu('gps', 'GPS', <IconCurrentLocation size={15} aria-hidden="true" />, f.gps, gpsOpts)}
            {anyF && <button type="button" className={s.ico} aria-label="Bỏ mọi lọc" title="Bỏ mọi lọc" onClick={() => setF(EMPTY_FILTER)}><IconFilterOff size={16} aria-hidden="true" /></button>}
          </div>
          <div className={s.selRow}>
            <button type="button" role="checkbox" aria-checked={allOn} aria-label="Chọn mọi tuyến đang hiện" className={s.chkHit} onClick={togAll}><span className={`${s.chk} ${allOn ? s.chkOn : ''}`}>{allOn && <IconCheck size={12} stroke={3} aria-hidden="true" />}</span></button>
            <span className={s.cnt} title={`${rows.length} tuyến khớp lọc / ${list.length} tuyến mẫu`}>{`${rows.length} / ${list.length}`}</span>
            {state === 'noperm' && <span className={s.note} role="status">{t('tms.viewOnly')}</span>}
          </div>
        </div>
        <div className={s.lcList} role="table" aria-label="Tuyến">
          {rows.length === 0 ? (
            <div className={s.none}>{t('tms.noRouteMatch')}<Button variant="secondary" icon={<IconFilterOff size={16} />} onClick={() => setF(EMPTY_FILTER)}>{t('tms.clearFilter')}</Button></div>
          ) : rows.map((x) => {
            const g = x.gps;
            const on = sel.has(x.code);
            return (
              <div key={x.code} role="row" aria-selected={on} className={`${s.rw} ${on ? s.rwOn : ''}`}>
                <button type="button" role="checkbox" aria-checked={on} aria-label={`Chọn ${x.code}`} className={s.chkHit} onClick={() => togOne(x.code)}><span className={`${s.chk} ${on ? s.chkOn : ''}`}>{on && <IconCheck size={12} stroke={3} aria-hidden="true" />}</span></button>
                <button type="button" className={s.rwMain} title={`${x.code} · ${[x.from, ...x.warehouses].map((c) => DP[c] ?? whName(c)).join(' → ')}`} onClick={noWrite}>
                  <span className={s.rwIc} style={{ background: `color-mix(in srgb, ${CG_DOT[x.cg]} 14%, transparent)` }} aria-hidden="true"><IconRoute size={17} stroke={1.9} /></span>
                  <span className={s.rwTx}><b>{whName(x.warehouses[0]!)}</b><span>{[x.code, fromName(x), x.tier, x.tolls ? `${x.tolls} trạm` : ''].filter(Boolean).join(' · ')}</span></span>
                  <span className={s.rwR}>
                    <span className={s.rwTm} title={g ? `Thời gian chạy TB ${fmtFull(g[0])} · ${g[3]} chuyến GPS` : `Chưa có chuyến GPS · ước tính ${fmtFull(x.est)}`} style={{ color: g ? 'var(--tx)' : 'var(--lb)' }}>
                      {g ? <IconClock size={13} aria-hidden="true" /> : <IconCurrentLocation size={13} aria-hidden="true" />}{g ? fmtHM(g[0]) : `≈${fmtHM(x.est)}`}
                    </span>
                    <span className={s.rwKm}>{`${fmtKm(x.km)} km`}
                      {expiring(x) && <span className={s.expI} role="img" aria-label={`Giá TRF hết hạn ${x.priceTo} · ${daysToExpiry(x)} ngày`}><IconCalendarDue size={13} aria-hidden="true" /></span>}
                      <span className={s.sDot} role="img" aria-label={STATUS_LABEL[x.status]} title={STATUS_LABEL[x.status]} style={{ background: STATUS_DOT[x.status] }} />
                    </span>
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      </section>
      <section className={s.rc} aria-label="Tổng quan tuyến">
        <h2><IconChartBar size={17} aria-hidden="true" />Tổng quan tuyến</h2>
        {bars('tier', 'Theo tier', <IconChartBar size={15} aria-hidden="true" />, f.tier, ['Tier1', 'Tier2', 'Tier3', 'Tier4'].map((v) => ({ value: v, label: v, n: list.filter((x) => x.tier === v).length })))}
        {bars('from', 'Theo điểm đi', <IconAnchor size={15} aria-hidden="true" />, f.from, Object.keys(DP).map((c) => ({ value: c, label: DP[c]!, n: list.filter((x) => x.from === c).length })).filter((i) => i.n > 0))}
        {bars('status', 'Trạng thái', <IconCheck size={15} aria-hidden="true" />, f.status, (['pend', 'ok', 'off'] as const).map((v) => ({ value: v, label: STATUS_LABEL[v], n: list.filter((x) => x.status === v).length })))}
      </section>
    </div>
  );

  const body = (
    <>
      <div className={s.bar}>
        <label className={s.search}>
          <IconSearch size={15} stroke={2.2} aria-hidden="true" />
          <input aria-label="Tìm mã tuyến, kho, khách" placeholder="Tìm mã tuyến, kho, khách" value={f.q} onChange={(e) => set({ q: e.target.value })} />
        </label>
        <div className={s.seg} role="group" aria-label="Loại hàng">
          {([['all', 'Tất cả'], ['RF', 'RF'], ['DC', 'Khô']] as const).map(([k, label]) => (
            <button key={k} type="button" aria-pressed={f.cg === k} onClick={() => set({ cg: k })}>{label}<span>{cnt('cg', (x) => k === 'all' || x.cg === k)}</span></button>
          ))}
        </div>
        {menu('from', 'Điểm đi', <IconAnchor size={15} aria-hidden="true" />, f.from, fromOpts)}
        {menu('wh', 'Kho / khách', <IconBuildingWarehouse size={15} aria-hidden="true" />, f.wh, whOpts)}
        {menu('tier', 'Tier', <IconUsers size={15} aria-hidden="true" />, f.tier, tierOpts)}
        {menu('status', 'Trạng thái', <IconCheck size={15} aria-hidden="true" />, f.status, stOpts)}
        {menu('gps', 'GPS', <IconCurrentLocation size={15} aria-hidden="true" />, f.gps, gpsOpts)}
        {anyF && <button type="button" className={s.ico} aria-label="Bỏ mọi lọc" title="Bỏ mọi lọc" onClick={() => setF(EMPTY_FILTER)}><IconFilterOff size={16} aria-hidden="true" /></button>}
        <span className={s.grow} />
        <span className={s.cnt} title={`${rows.length} tuyến khớp lọc / ${list.length} tuyến mẫu`}>{`${rows.length} / ${list.length}`}</span>
        <div className={`${s.seg} ${s.segS}`} role="group" aria-label="Mật độ">
          <button type="button" aria-pressed={!dense} onClick={() => setDense(false)}>{t('tms.densityStd')}</button>
          <button type="button" aria-pressed={dense} onClick={() => setDense(true)}>{t('tms.densityCompact')}</button>
        </div>
      </div>
      {state === 'noperm' && <div className={s.note} role="status">{t('tms.viewOnly')}</div>}
      <section className={`${s.card} ${dense ? s.dense : ""}`} aria-label="Tuyến">
        {rows.length === 0 ? (
          <div className={s.none}>{t('tms.noRouteMatch')}<Button variant="secondary" icon={<IconFilterOff size={16} />} onClick={() => setF(EMPTY_FILTER)}>{t('tms.clearFilter')}</Button></div>
        ) : (
          <Table columns={cols} rows={rows} rowKey={(x) => x.code} label="Tuyến" selected={sel} onSelect={setSel} onRowClick={noWrite} dense={dense} selectAllLabel="Chọn mọi tuyến đang hiện" selectLabel={(k) => `Chọn ${k}`} />
        )}
      </section>
    </>
  );

  return (
    <Page kind="table">
      <PageHeader findPad
        title="Tuyến"
        findHint={false}
        tabs={
          <span className={s.hChips}>
            {state === 'ready' || state === 'noperm' ? <span className={s.tot} title="Trong danh mục tuyến">{`${TOTAL} tuyến`}</span> : null}
            {(state === 'ready' || state === 'noperm') && chips.pend > 0 && <button type="button" className={`${s.hChip} ${s.warn}`} aria-pressed={f.status.length === 1 && f.status[0] === 'pend'} title="Tuyến chờ duyệt (NeedVerify)" onClick={() => set({ status: f.status.length === 1 && f.status[0] === 'pend' ? [] : ['pend'] })}><IconCheck size={14} aria-hidden="true" /><b>{chips.pend}</b>{t('tms.awaitApproval')}</button>}
            {(state === 'ready' || state === 'noperm') && chips.noGps > 0 && <button type="button" className={s.hChip} aria-pressed={f.gps.length === 1 && f.gps[0] === 'no'} title="Tuyến chưa có chuyến GPS · thời gian đang là ước tính" onClick={() => set({ gps: f.gps.length === 1 && f.gps[0] === 'no' ? [] : ['no'] })}><IconCurrentLocation size={14} aria-hidden="true" /><b>{chips.noGps}</b>{t('tms.noGps')}</button>}
            {(state === 'ready' || state === 'noperm') && chips.expiring > 0 && <button type="button" className={`${s.hChip} ${s.warn}`} aria-pressed={f.expiring} title="Giá TRF hết hạn trong 14 ngày" onClick={() => set({ expiring: !f.expiring })}><IconCalendarDue size={14} aria-hidden="true" /><b>{chips.expiring}</b>{t('tms.priceExpiring')}</button>}
          </span>
        }
        actions={
          <>
            <button type="button" className={s.ico} aria-label="Nhập Excel" title="Nhập Excel" onClick={noWrite}><IconUpload size={18} aria-hidden="true" /></button>
            <button type="button" className={s.ico} aria-label="Xuất Excel" title="Xuất Excel" onClick={() => toast({ msg: `Đã xuất Excel · ${rows.length} tuyến` })}><IconDownload size={18} aria-hidden="true" /></button>
            <button type="button" className={s.ico} aria-label="Mở bản đồ điểm & tuyến" title="Mở bản đồ điểm & tuyến" onClick={() => void navigate({ to: '/ezy/tms/places' })}><IconMap size={18} aria-hidden="true" /></button>
            <Button variant="primary" icon={<IconPlus size={16} />} onClick={noWrite}>{t('tms.addRoute')}</Button>
          </>
        }
      />
      <div className={s.page}>
        <StateView
          state={state === 'noperm' ? 'ready' : state}
          icon={<IconRoute size={26} stroke={1.8} />}
          title={state === 'empty' ? 'Chưa có tuyến nào' : undefined}
          action={state === 'error' ? <Button variant="secondary" onClick={() => void refetch()}>{t('common.retry')}</Button> : state === 'empty' ? <Button variant="secondary" icon={<IconPlus size={16} />} onClick={noWrite}>{t('tms.addRoute')}</Button> : undefined}
          skeleton={<div className={`${s.card} ${s.skel}`} aria-busy="true">{Array.from({ length: 8 }, (_, i) => <div key={i} />)}</div>}
        >
          {narrow ? bodyPad : body}
        </StateView>
      </div>
    </Page>
  );
}
